import type { FunctionComponent } from "react";
import { useCallback, useMemo, useState } from "react";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { Text } from "@smartsuite/react-ui/lib";
import type { Job } from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../../../../stores/hooks";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import { SubConditionBox } from "../SubConditionBox/SubConditionBox";
import {
  formatBranchConditions,
  formatMatchedConditionValues,
} from "./format-branch-conditions";
import {
  conditionReferences,
  referenceValueFromJob,
  referenceValueFromMatchedOutputs,
} from "./resolve-matched-values";
import { useBranchOutputs } from "../../hooks/useLazyOutputs";
import { SubConditionItemSkeleton } from "../SubConditionItem/SubConditionItemSkeleton";
import type { BranchView } from "./utils";
import type { RenderNestedGroups } from "./BranchesGroup";
import {
  StyledBranchContent,
  StyledConditionLine,
  StyledFadeIn,
  StyledStaticBox,
} from "./styles";

interface BranchCardProps {
  branch: BranchView;
  job: Job;
  recordIndex: number;
  renderNestedGroups: RenderNestedGroups;
  blockInstanceId: number;
}

export const BranchCard: FunctionComponent<BranchCardProps> = observer(
  ({ branch, job, recordIndex, renderNestedGroups, blockInstanceId }) => {
    const { automations, members, teams } = useRootStore();
    // ActivityDetailsGroup keeps its own expand/collapse state internally and
    // doesn't expose it, so the `renderNestedGroups` render-prop call below
    // (which recurses into nested action groups) is gated on a second, local
    // toggle kept in lockstep via ActivityDetailsGroup's onClick callback --
    // otherwise the call would fire the moment this card mounts (i.e. as
    // soon as the *block* expands), regardless of whether this branch's own
    // content is actually visible.
    const [isExpanded, setIsExpanded] = useState(false);
    const handleToggle = useCallback(() => setIsExpanded((prev) => !prev), []);

    // Both are only consumed inside the isExpanded-gated subtree below, so
    // skip the work entirely while collapsed instead of recomputing it (via
    // real PhraseBuilder/output-resolution calls) on every render.
    const conditionLines = useMemo(
      () =>
        isExpanded && !branch.isElse && branch.conditions !== undefined
          ? formatBranchConditions(
              branch.conditions,
              automations.candidate,
              blockInstanceId,
              { members: members.list, teams: teams.list },
            )
          : [],
      [
        isExpanded,
        branch.isElse,
        branch.conditions,
        automations.candidate,
        blockInstanceId,
        members.list,
        teams.list,
      ],
    );
    // api 7.25.0 stamps a branchId per executed branch; when present the
    // matched values come from GetBranchData (the engine resolved the exact
    // values the conditions were evaluated against). "" marks a pre-7.25 job
    // and falls back to the client-side reference walk.
    const usesBranchData =
      isExpanded &&
      !branch.isElse &&
      branch.conditions !== undefined &&
      branch.branchId !== "";
    const branchOutputs = useBranchOutputs(
      job.jobId,
      branch.branchId,
      usesBranchData,
    );
    // Per the Run History design a matched row is a single
    // "[Source].[Field]: Value" line per condition reference -- built with
    // the same label machinery as conditionLines; only the value strategy
    // differs between the engine path and the legacy walk.
    const matchedLines = useMemo(() => {
      if (!isExpanded || branch.isElse || branch.conditions === undefined) {
        return [];
      }
      const legacyResolve = referenceValueFromJob(job, recordIndex);
      // STR-14148: on the engine path BranchData carries ONLY the outputs of
      // the conditions to list as matched (all conditions of a passing AND
      // group; the first passed condition of an OR group), so row resolution
      // is gated on presence in that payload -- see
      // referenceValueFromMatchedOutputs. The legacy walk (pre-7.25 jobs,
      // empty branchId) has no verdicts to gate on and keeps listing every
      // reference that resolves.
      const resolveValue =
        branch.branchId !== ""
          ? referenceValueFromMatchedOutputs(
              branchOutputs,
              job.automation?.timezone,
              conditionReferences(branch.conditions),
              legacyResolve,
            )
          : legacyResolve;
      return formatMatchedConditionValues(
        branch.conditions,
        automations.candidate,
        blockInstanceId,
        resolveValue,
      );
    }, [
      isExpanded,
      branch.isElse,
      branch.conditions,
      branch.branchId,
      branchOutputs,
      job,
      recordIndex,
      automations.candidate,
      blockInstanceId,
    ]);

    // The engine path can distinguish "still loading" (undefined) from
    // "settled" ([]), so it shows a skeleton while GetBranchData is in
    // flight -- otherwise a pending fetch would be indistinguishable from
    // "nothing matched".
    const matchedLoading = usesBranchData && branchOutputs === undefined;

    return (
      <ActivityDetailsGroup label={branch.label} onClick={handleToggle}>
        {isExpanded && (
          <StyledBranchContent>
            {branch.isElse ? (
              // The else branch has no conditions to list, so its label is a
              // plain static box -- not an (empty) expandable SubConditionBox.
              <StyledStaticBox>
                <Text size="m">{t("job-list.condition-when-not-met")}</Text>
              </StyledStaticBox>
            ) : (
              <SubConditionBox title={t("job-list.condition-when-met")}>
                {conditionLines.map((line, index) => (
                  <StyledConditionLine key={index}>
                    <Text size="m">
                      <Trans
                        components={{
                          // format-branch-conditions tags the field NAME
                          // <field>; per the Run History design it renders
                          // medium-weight. The source prefix, brackets,
                          // operator and value inherit this line's regular
                          // weight.
                          field: <Text tag="span" size="m" weight="medium" />,
                        }}
                      >
                        {line.text}
                      </Trans>
                    </Text>
                    {line.connector !== undefined && (
                      <Text size="m" weight="semibold">
                        {line.connector}
                      </Text>
                    )}
                  </StyledConditionLine>
                ))}
              </SubConditionBox>
            )}

            {matchedLoading ? (
              <SubConditionItemSkeleton testId="matched-conditions-skeleton" />
            ) : (
              matchedLines.length > 0 && (
                <StyledFadeIn>
                  <SubConditionBox title={t("job-list.matched-conditions")}>
                    {matchedLines.map((line) => (
                      <StyledConditionLine key={line.key}>
                        {/* Structured parts rendered as plain React children
                            (never a markup string parsed by Trans), so record
                            values containing tag-like text stay inert. Same
                            weighting as the condition lines: only the field
                            NAME is medium. */}
                        <Text size="m">
                          {line.sourceLabel !== undefined
                            ? `[${line.sourceLabel}].[`
                            : "["}
                          <Text tag="span" size="m" weight="medium">
                            {line.fieldLabel}
                          </Text>
                          {`]: ${line.value}`}
                        </Text>
                      </StyledConditionLine>
                    ))}
                  </SubConditionBox>
                </StyledFadeIn>
              )
            )}

            {renderNestedGroups(branch.actionGroups, recordIndex)}
          </StyledBranchContent>
        )}
      </ActivityDetailsGroup>
    );
  },
);
